import {Box, Divider, Flex, Text} from '@chakra-ui/react';
import {IconChevronsDown, IconChevronsRight} from '@tabler/icons';
import {theme} from '../../style/theme';

const VerticalLine = (
  <Flex
    display={{base: 'flex', lg: 'none'}}
    flexDir="column"
    alignItems="center"
  >
    <Box zIndex={1} className="vertical-icon">
      <IconChevronsDown
        color={theme.colors.aqua['500']}
        size="50px"
        stroke={3}
      />
    </Box>
    <Divider
      orientation="vertical"
      className="vertical-separator"
      sx={{bgColor: 'buttonGrey'}}
    />
  </Flex>
);

const HorizontalLine = (
  <Flex flex={1} display={{base: 'none', lg: 'flex'}}>
    <Flex alignItems="center" width="25%">
      <Divider
        orientation="horizontal"
        sx={{bgColor: 'buttonGrey'}}
        className="horizontal-start-separator"
      />
    </Flex>
    <Flex alignItems="center" width="75%">
      <Box zIndex={1} className="horizontal-icon">
        <IconChevronsRight
          color={theme.colors.aqua['500']}
          size="50px"
          stroke={3}
        />
      </Box>
      <Divider
        orientation="horizontal"
        sx={{bgColor: 'buttonGrey'}}
        className="horizontal-separator"
      />
    </Flex>
  </Flex>
);

export interface TimelineSegmentProps {
  title: string;
  description: string;
}

export const TimelineSegment: React.FC<TimelineSegmentProps> = ({
  title,
  description,
}) => (
  <Flex
    px={{base: 10, md: 0}}
    height={{base: 200, lg: 150}}
    flexDir={{lg: 'column'}}
    className="timeline-segment"
  >
    {VerticalLine}
    {HorizontalLine}
    <Flex flexDir="column" pt={2} pr={{md: 6, lg: 16}} maxW={{lg: '300px'}}>
      <Text mb={2} textStyle="h6" color="darkGrey">
        {title}
      </Text>
      <Text textStyle="body2" textAlign="start">
        {description}
      </Text>
    </Flex>
  </Flex>
);

type TimelineItem = {
  title: string;
  description: string;
  id: string;
};

interface TimlineProps {
  items: [TimelineItem];
  heading?: string;
  background?: string;
}

export const Timeline: React.FC<TimlineProps> = ({
  items,
  heading,
  background,
}) => {
  return (
    <Box bg={background}>
      {heading && (
        <Text
          textAlign="center"
          textStyle={{base: 'h5', lg: 'h4'}}
          display="block"
          pb={{base: 10, lg: 0}}
        >
          {heading}
        </Text>
      )}
      <Flex
        pt={{md: 10, lg: 20}}
        pr={{md: 6, lg: 12}}
        pl={{md: 10, lg: 20}}
        pb={{base: 10, md: 20, lg: 150}}
        flexDir={{base: 'column', lg: 'row'}}
        maxW="1330px"
        margin="0 auto"
        sx={{
          '.timeline-segment:first-of-type': {
            // start
            '.vertical-icon': {
              height: '35px',
            },
            '.vertical-separator': {
              height: '170px',
            },
            '.horizontal-icon': {
              width: '40px',
            },
            '.horizontal-separator': {
              width: '100%',
            },
            '.horizontal-start-separator': {
              display: 'none',
            },
          },
          '.timeline-segment:not(:first-of-type)': {
            // middle
            '.vertical-icon': {
              height: '0px',
            },
            '.vertical-separator': {
              height: '200px',
            },
            '.horizontal-icon': {
              width: '0px',
            },
            '.horizontal-separator': {
              width: '100%',
            },
          },
          '.timeline-segment:last-of-type': {
            // end
            '.vertical-icon': {
              height: '0px',
            },
            '.vertical-separator': {
              height: '25px',
            },
            '.horizontal-icon': {
              width: '0px',
            },
            '.horizontal-separator': {
              width: '20px',
            },
          },
        }}
      >
        {items &&
          items.map((item) => (
            <TimelineSegment
              key={item.title}
              title={item.title}
              description={item.description}
            />
          ))}
      </Flex>
    </Box>
  );
};
